import React, { useState, useEffect } from 'react';
import LocalizedText from '@LocalizedText';
import PropTypes from 'prop-types';
import { zoomToPercent, zoomToIndex } from '@app/utils/utils';

import './ZoomControls.scss';

const increaseZoom = (currentZoom, maxZoom, setNewZoom) => {
  const increasedZoom = parseFloat(currentZoom) + 1;
  increasedZoom <= zoomToPercent(maxZoom) && setNewZoom(increasedZoom);
};

const decreaseZoom = (currentZoom, minZoom, setNewZoom) => {
  const decreasedZoom = parseFloat(currentZoom) - 1;
  decreasedZoom >= zoomToPercent(minZoom) && setNewZoom(decreasedZoom);
};

const validZoomValue = (newZoom, minZoom, maxZoom, currentZoom) => {
  if (!newZoom) return currentZoom;
  const maxZoomPercent = zoomToPercent(maxZoom);
  const minZoomPercent = zoomToPercent(minZoom);
  const newZoomPercent = parseFloat(newZoom);

  if (newZoomPercent < minZoomPercent) return minZoom;
  if (newZoomPercent > maxZoomPercent) return maxZoom;

  return zoomToIndex(newZoomPercent);
};

const filterInput = ({ target: { value: inputValue } }) => {
  const nonNumericReg = new RegExp(/[^0-9.]|^\./g);
  const floatSequenceReg = new RegExp(/^([0-9]+\.[0-9]+)\.|(\.)\./);

  return inputValue.replace(',', '.').replace(nonNumericReg, '').replace(floatSequenceReg, '$1$2');
};

const ZoomControls = ({ currentZoom, minZoom, maxZoom, setZoomLevel, scrollToCenter, setZoomToInputValue, autoZoom }) => {
  const [newZoom, setNewZoom] = useState(0);

  useEffect(() => {
    setNewZoom(zoomToPercent(currentZoom));
  }, [currentZoom]);

  useEffect(() => {
    setZoomToInputValue(newZoom);
  }, [newZoom, setZoomToInputValue]);

  const handleKeyDown = e => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      increaseZoom(newZoom, maxZoom, setNewZoom);
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      decreaseZoom(newZoom, minZoom, setNewZoom);
    }

    if (e.key === 'Enter') {
      const validZoom = validZoomValue(newZoom, minZoom, maxZoom, currentZoom);

      setNewZoom(zoomToPercent(validZoom));

      setZoomLevel(validZoom);
      scrollToCenter && scrollToCenter();
    }
  };

  const handleOnChange = e => {
    const zoomValue = filterInput(e);
    setNewZoom(zoomValue);
  };

  const handleDragStart = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div id="zoom-controls-container">
      <div aria-hidden className="zoom-fit-to-screen" onClick={() => autoZoom()}>
        <LocalizedText id="qau-fit-to-screen" />
      </div>
      <div className="zoom-to-container" key={currentZoom}>
        <input
          type="text"
          value={newZoom || ''}
          className="zoom-to-input"
          onKeyDown={handleKeyDown}
          onChange={handleOnChange}
          onDragStart={handleDragStart}
        />
        %
      </div>
    </div>
  );
};

ZoomControls.propTypes = {
  currentZoom: PropTypes.number.isRequired,
  minZoom: PropTypes.number.isRequired,
  maxZoom: PropTypes.number.isRequired,
  scrollToCenter: PropTypes.func,
  setZoomLevel: PropTypes.func.isRequired,
  autoZoom: PropTypes.func.isRequired,
  setZoomToInputValue: PropTypes.func.isRequired,
};

export default ZoomControls;
